import { keys } from "lodash";
import * as Yup from "yup";

import {
  POONAWALLA_USER_ROLES_TYPES,
  RESET_PASSWORD_TYPES,
  _API_EXECUTION_TYPES,
  _API_GATEWAY_TYPES,
  _API_SCOPES,
} from "../constants";

// AUTH SCHEMAS

const bodySchemaValidation = (value) => {
  if (value !== undefined) {
    return "Validation schema is mandatory";
  }
};

export const SignupSchema = (captchaValue) =>
  Yup.object().shape({
    first_name: Yup.string()
      .matches(/^[aA-zZ]+$/, "Special characters are not allowed.")
      .required("First name is mandatory"),
    last_name: Yup.string()
      .matches(/^[aA-zZ]+$/, "Special characters are not allowed.")
      .required("Last name is mandatory"),
    email: Yup.string()
      .email("Email is invalid")
      .required("Email is mandatory"),
    phone_number: Yup.string().required("Phone Number is mandatory"),
    customer_type: Yup.string().required("Customer Id is mandatory"),
    user_type: Yup.string()
      .oneOf(keys(POONAWALLA_USER_ROLES_TYPES.lowPrivileged))
      .required("User Type is mandatory"),
    company_name: Yup.string().when("user_type", {
      is: "CORPORATE",
      then: Yup.string()
        .matches(/^[A-za-z0-9\s]+$/, "Special characters are not allowed.")
        .required("Company name is mandatory"),
    }),
    project_name: Yup.string().when("user_type", {
      is: "INDIVIDUAL",
      then: Yup.string()
        .matches(/^[A-za-z0-9\s]+$/, "Special characters are not allowed.")
        .required("Project Name is mandatory"),
    }),
    purpose: Yup.string().when("user_type", {
      is: "INDIVIDUAL",
      then: Yup.string()
        .matches(
          /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
          "Special characters are not allowed."
        )
        .required("Purpose is mandatory"),
    }),
    tnc: Yup.boolean().oneOf(
      [true],
      "Please accept Terms and Conditions to continue"
    ),
    ...(captchaValue && {
      captcha: Yup.string()
        .length(5, "Captcha must be exactly 5 characters.")
        .required("Please fill out the captcha"),
    }),
  });

export const LoginSchema = (captchaValue) =>
  Yup.object().shape({
    email: Yup.string()
      .email("Email is invalid.")
      .required("Email is required."),
    password: Yup.string().required("Password is required."),
    ...(captchaValue && {
      captcha: Yup.string()
        .length(5, "Captcha must be exactly 5 characters.")
        .required("Please fill out the captcha."),
    }),
  });

export const MFALoginSchema = (captchaValue) =>
  Yup.object().shape({
    challenge_code: Yup.string().required("MFA Token is required."),
    ...(captchaValue && {
      captcha: Yup.string()
        .length(5, "Captcha must be exactly 5 characters.")
        .required("Please fill out the captcha."),
    }),
  });

export const OTPVerificationSchema = (captchaValue) =>
  Yup.object().shape({
    otp: Yup.string().required("OTP is required."),
    ...(captchaValue && {
      captcha: Yup.string()
        .length(5, "Captcha must be exactly 5 characters.")
        .required("Please fill out the captcha."),
    }),
  });

export const EmailVerificationSchema = (captchaValue) =>
  Yup.object().shape({
    challenge_code: Yup.string().required("OTP is required."),
    ...(captchaValue && {
      captcha: Yup.string()
        .length(5, "Captcha must be exactly 5 characters.")
        .required("Please fill out the captcha."),
    }),
  });

export const ForgotPasswordSchema = (captchaValue) =>
  Yup.object().shape({
    email: Yup.string()
      .email("Email is invalid.")
      .required("Email is required."),
    ...(captchaValue && {
      captcha: Yup.string()
        .length(5, "Captcha must be exactly 5 characters.")
        .required("Please fill out the captcha."),
    }),
  });

export const ChangePasswordSchema = (type, captchaValue) =>
  Yup.object().shape({
    email: Yup.string()
      .email("Email is invalid.")
      .required("Email is required."),
    ...(type === RESET_PASSWORD_TYPES.CHANGE_PASSWORD && {
      old_password: Yup.string().required("Old Password is required."),
    }),
    ...(type === RESET_PASSWORD_TYPES.FORGOT_PASSWORD && {
      challenge_code: Yup.string().required("OTP is required."),
    }),
    password: Yup.string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/,
        "Password is not strong."
      )
      .required("New Password is required."),
    confirm_password: Yup.string()
      .required("Re-Enter New Password.")
      .oneOf([Yup.ref("password")], "Password must match."),
    ...(captchaValue && {
      captcha: Yup.string()
        .length(5, "Captcha must be exactly 5 characters.")
        .required("Please fill out the captcha."),
    }),
  });

// APPLICATION SCHEMAS

export const SelectProductTypeSchema = () =>
  Yup.object().shape({
    spt: Yup.string().required("Select at least one product type"),
  });

export const ValidateAppNameSchema = () =>
  Yup.object().shape({
    name: Yup.string()
      .nullable()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("Application Name is mandatory"),
  });

export const UpdateAppNameSchema = () =>
  Yup.object().shape({
    name: Yup.string().required("Application Name is mandatory"),
    new_name: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("Application Name is mandatory"),
  });

export const FeedbackSchema = () =>
  Yup.object().shape({
    type: Yup.string().required("Please select your feedback type."),
    email: Yup.string()
      .email("Email is invalid.")
      .required("Email is required."),
    message: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("Please mention your thoughts.")
      .min(10, "Atleast 10 words are mandatory."),
  });

export const CreateCategorySchema = () =>
  Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("Category Name is mandatory"),
    description: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("Description is mandatory"),
    banner_url: Yup.string().required("Banner is mandatory"),
    status: Yup.string()
      .oneOf(["ACTIVE", "INACTIVE"])
      .required("Status is mandatory"),
  });

export const CreateSecretSchema = () =>
  Yup.object().shape({
    key: Yup.string()
      .matches(/^[a-zA-Z0-9_-]*$/, "Special characters are not allowed.")
      .required("Key is mandatory"),
    value: Yup.string().required("Value is mandatory"),
    description: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("Description is mandatory"),
    environment: Yup.string().required("Environment is mandatory"),
  });

export const CreateCertificateSchema = () =>
  Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("Name is mandatory"),
    description: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("Description is mandatory"),
    certificate: Yup.string().test(
      "oneOfRequired",
      "It is mandatory to provide information in one of these fields for successful submission.",
      function (item) {
        return (
          this.parent.certificate ||
          [this.parent.certificate, this.parent.pfx, this.parent.key].filter(
            (e) => e === undefined
          ).length <= 2
        );
      }
    ),
    key: Yup.string().test(
      "oneOfRequired",
      " It is mandatory to provide information in one of these fields for successful submission.",
      function (item) {
        return (
          this.parent.key ||
          [this.parent.key, this.parent.certificate, this.parent.pfx].filter(
            (e) => e === undefined
          ).length <= 2
        );
      }
    ),
    pfx: Yup.string().test(
      "oneOfRequired",
      " It is mandatory to provide information in one of these fields for successful submission.",
      function (item) {
        return (
          this.parent.pfx ||
          [this.parent.pfx, this.parent.certificate, this.parent.key].filter(
            (e) => e === undefined
          ).length <= 2
        );
      }
    ),
  });

export const CreateScheduledJobSchema = () =>
  Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("Name is mandatory"),
    description: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("Description is mandatory"),
    environment: Yup.string().required("Environment is mandatory"),
    user_id: Yup.mixed().required("User is mandatory."),
    // workflow_id: Yup.string("Workflow is mandatory."),
    // api_id: Yup.string("Api is mandatory"),
    cron_expression: Yup.string().required("Cron Expression is mandatory"),
  });

export const CreateAPIGatewaySchema = () =>
  Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("API Gateway Name is required."),
    description: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("API Gateway Description is required."),
    aws_account_id: Yup.string().required("AWS Account ID is required."),
    management_assume_role_arn: Yup.string().notRequired(),
    api_gateway_id: Yup.string().required("API Gateway Id is required."),
    region: Yup.string().required("API Gateway region is required."),
    type: Yup.string().required("API Gateway Type is required."),
    scope: Yup.string().required("API Gateway Scope is required."),
    root_resource_id: Yup.string().required(
      "API Gateway Root Resource Id is required."
    ),
    api_gateway_request_validator_id: Yup.string().notRequired(),
    management_access_key_id: Yup.string().notRequired(),
    management_secret_access_key: Yup.string().notRequired(),
  });

export const UpdateAPIGatewaySchema = () =>
  Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .notRequired(),
    description: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .notRequired(),
    aws_account_id: Yup.string().notRequired(),
    management_assume_role_arn: Yup.string().notRequired(),
    api_gateway_id: Yup.string().notRequired(),
    region: Yup.string().required("API Gateway region is required."),
    type: Yup.string().notRequired(),
    scope: Yup.string().notRequired(),
    root_resource_id: Yup.string().notRequired(),
    api_gateway_request_validator_id: Yup.string().notRequired(),
    management_access_key_id: Yup.string().notRequired(),
    management_secret_access_key: Yup.string().notRequired(),
  });

export const CreateAPIGatewayStageSchema = () =>
  Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("API Gateway Stage Name is required."),
    description: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("API Gateway Stage Description is required."),
    api_gateway_id: Yup.string().required("API Gateway Id is required."),
    stage: Yup.string().required("API Gateway Stage is required."),
    gateway_endpoint: Yup.string()
      .matches(
        /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/,
        "API Gateway Stage Endpoint is invalid."
      )
      .required("API Gateway Stage Endpoint is required."),
  });

export const UpdateAPIGatewayStageSchema = () =>
  Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .notRequired(),
    description: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .notRequired(),
    api_gateway_id: Yup.string().notRequired(),
    stage: Yup.string().notRequired(),
    gateway_endpoint: Yup.string().notRequired(),
  });

export const CreateWorkflowsSchema = () =>
  Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("Name is mandatory"),
    description: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("Description is mandatory"),
    environment: Yup.string().required("Environment is mandatory"),
  });

export const CreateCustomerEndpointValidationSchema = () =>
  Yup.object().shape({
    user_id: Yup.mixed().required("Target Customer is mandatory."),
    api_id: Yup.mixed().required("API is mandatory."),
    new_target_endpoint: Yup.string()
      .matches(
        /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/,
        "URL is invalid."
      )
      .required("New Target API URL is mandatory."),
    description: Yup.string().required("Description is mandatory."),
  });

export const ApiSetupSetp1Schema = () =>
  Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("API Name is mandatory."),
    scope: Yup.string()
      .oneOf(["PUBLIC", "PRIVATE"])
      .required("API Scope is mandatory."),
    description: Yup.string()
      .matches(
        /^(?=[a-zA-Z0-9~@#$^()_+[\]{}\\,.?: -]*$)(?!.*[<>'"=/;`%])/,
        "Special characters are not allowed."
      )
      .required("API Description is mandatory."),
    format: Yup.string()
      .oneOf(["REST", "SOAP"])
      .required("API Format is mandatory."),
    method: Yup.string()
      .oneOf(["GET", "POST", "PUT", "DELETE", "PATCH"])
      .required("API Method is mandatory."),
    category_id: Yup.string().required("API Category is mandatory."),
    environment: Yup.string().required("API Environment is mandatory."),
    api_gateway_id: Yup.string().when("scope", {
      is: (scope) => !!scope,
      then: Yup.string().required("API Gateway ID is required."),
      otherwise: Yup.string(),
    }),
    api_gateway_stage_id: Yup.string().when("api_gateway_id", {
      is: (apiGatewayId) => !!apiGatewayId,
      then: Yup.string().required("API Gateway Stage ID is required."),
      otherwise: Yup.string(),
    }),
    authentication_type: Yup.string().required(
      "Authentication Type is mandatory."
    ),
  });

export const ApiSetupSetp2Schema = () =>
  Yup.object().shape({
    target_endpoint: Yup.string()
      .nullable()
      .when("execution_type", {
        is: _API_EXECUTION_TYPES.API_PROXY,
        then: Yup.string()
          .nullable()
          .matches(
            /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/,
            "URL is invalid."
          )
          .required("Target Endpoint is mandatory."),
      }),
    workflow_id: Yup.string()
      .nullable()
      .when("execution_type", {
        is: _API_EXECUTION_TYPES.WORKFLOW,
        then: Yup.string().nullable().required("Workflow is mandatory."),
      }),
    endpoint: Yup.string().required("Endpoint is required."),
    header_field: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Name is mandatory."),
        description: Yup.string().required("Description is mandatory."),
        mandatory: Yup.boolean(),
        value: Yup.string().required("Value is mandatory."),
        environment: Yup.string()
          .when("secretManagerKey", {
            is: false,
            then: Yup.string().notRequired(),
          })
          .when("secretManagerKey", {
            is: true,
            then: Yup.string().required("Environment is mandatory."),
          }),
        defaultvalue: Yup.string().nullable().notRequired(),
      })
    ),
    request_header_transformation: Yup.array().of(
      Yup.object().shape({
        action: Yup.string()
          .oneOf(["SET", "RENAME", "FILTER"])
          .required("Action is mandatory."),
        behaviour: Yup.string().when("action", {
          is: "SET",
          then: Yup.string()
            .oneOf(["OVERWRITE", "SKIP", "APPEND"])
            .required("Behaviour is mandatory."),
        }),
        type: Yup.string().when("action", {
          is: "FILTER",
          then: Yup.string()
            .oneOf(["ALLOW", "BLOCK"])
            .required("Type is mandatory."),
        }),
        name: Yup.string()
          .when("action", {
            is: "SET",
            then: Yup.string().required("Header name is mandatory."),
          })
          .when("action", {
            is: "RENAME",
            then: Yup.string().required("Header name is mandatory."),
          }),
        new_name: Yup.string().when("action", {
          is: "RENAME",
          then: Yup.string().required("New Header name is mandatory."),
        }),
        values: Yup.array()
          .when("action", {
            is: "FILTER",
            then: Yup.array().min(1, "Header Names are mandatory."),
          })
          .when("action", {
            is: "SET",
            then: Yup.array().min(1, "Values are mandatory."),
          }),
      })
    ),
    response_header_transformation: Yup.array().of(
      Yup.object().shape({
        action: Yup.string()
          .oneOf(["SET", "RENAME", "FILTER"])
          .required("Action is mandatory."),
        behaviour: Yup.string().when("action", {
          is: "SET",
          then: Yup.string()
            .oneOf(["OVERWRITE", "SKIP", "APPEND"])
            .required("Behaviour is mandatory."),
        }),
        type: Yup.string().when("action", {
          is: "FILTER",
          then: Yup.string()
            .oneOf(["ALLOW", "BLOCK"])
            .required("Type is mandatory."),
        }),
        name: Yup.string()
          .when("action", {
            is: "SET",
            then: Yup.string().required("Header name is mandatory."),
          })
          .when("action", {
            is: "RENAME",
            then: Yup.string().required("Header name is mandatory."),
          }),
        new_name: Yup.string().when("action", {
          is: "RENAME",
          then: Yup.string().required("New Header name is mandatory."),
        }),
        values: Yup.array()
          .when("action", {
            is: "FILTER",
            then: Yup.array().min(1, "Header Names are mandatory."),
          })
          .when("action", {
            is: "SET",
            then: Yup.array().min(1, "Values are mandatory."),
          }),
      })
    ),
    query_field: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Name is mandatory."),
        description: Yup.string().required("Description is mandatory."),
        mandatory: Yup.boolean(),
      })
    ),
    request_field: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Name is mandatory."),
        description: Yup.string().required("Description is mandatory."),
        mandatory: Yup.boolean(),
        datatype: Yup.string()
          .oneOf(["string", "integer", "number", "boolean", "array", "object"])
          .required("Datatype is mandatory."),
      })
    ),
    response_field: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Name is mandatory."),
        description: Yup.string().required("Description is mandatory."),
        datatype: Yup.string()
          .oneOf(["string", "integer", "number", "boolean", "array", "object"])
          .required("Datatype is mandatory."),
      })
    ),
  });

export const ApiSetupSetp3Schema = () =>
  Yup.object().shape({
    request_body_validation_schema: Yup.array().of(
      Yup.object().shape({
        type: Yup.string()
          .oneOf([
            "multipart/form-data",
            "application/json",
            "application/x-www-form-urlencoded",
          ])
          .required("Type is mandatory."),
        content: Yup.string().test(
          "bodySchemaValidation",
          "Validation schema is mandatory",
          bodySchemaValidation
        ),
      })
    ),
    sample_request: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Name is mandatory."),
        type: Yup.string()
          .oneOf([
            "multipart/form-data",
            "application/json",
            "application/xml",
            "text/plain",
            "application/x-www-form-urlencoded",
            "text/html",
          ])
          .required("Type is mandatory."),
        description: Yup.string().required("Description is mandatory."),
        content: Yup.mixed()
          .nullable()
          .required("Sample Request is mandatory."),
      })
    ),
    sample_response: Yup.array().of(
      Yup.object().shape({
        code: Yup.string().required("Status Code is mandatory."),
        success: Yup.boolean().required("Response Type is mandatory."),
        type: Yup.string()
          .oneOf([
            "multipart/form-data",
            "application/json",
            "application/xml",
            "text/plain",
            "application/x-www-form-urlencoded",
            "text/html",
          ])
          .required("Type is mandatory."),
        description: Yup.string().required("Description is mandatory."),
        content: Yup.mixed()
          .nullable()
          .required("Sample Response is mandatory."),
      })
    ),
    languages: Yup.array(),
    files: Yup.array(),
  });

export const ApiStatusChangeSchema = () =>
  Yup.object().shape({
    status: Yup.string()
      .oneOf(["ACTIVE", "INACTIVE", "DRAFT", "REJECTED", "CORRECTION_REQUIRED"])
      .required("New API Status is required."),
    message: Yup.string()
      .when("status", {
        is: "REJECTED",
        then: Yup.string().required("Reason is required for Rejection."),
      })
      .when("status", {
        is: "CORRECTION_REQUIRED",
        then: Yup.string().required("Reason is required for Correction."),
      }),
  });

export const CreateUserSchema = () =>
  Yup.object().shape({
    first_name: Yup.string()
      .matches(/^[aA-zZ]+$/, "Special characters are not allowed.")
      .required("First name is mandatory"),
    last_name: Yup.string()
      .matches(/^[aA-zZ]+$/, "Special characters are not allowed.")
      .required("Last name is mandatory"),
    email: Yup.string()
      .email("Email is invalid")
      .required("Email is mandatory"),
    phone_number: Yup.string().required("Phone Number is mandatory"),
    user_type: Yup.string().required("User Type is mandatory"),
  });
